import { Tab } from '@mui/material'
import { Tabs } from '@workwave-tidal/core/components'
import { useSelector } from 'react-redux'

import { Tooltip } from '@/components/primitives/Tooltip'
import { selectUserConfiguration } from '@/features/domain/user'

import { useTexts } from '../hooks/useTexts'

interface Props {
  tab: number
  setTab: (tab: number) => void
}

export function PreviewTabs(props: Props) {
  const { tab, setTab } = props
  const texts = useTexts()

  const userConfig = useSelector(selectUserConfiguration)
  const isSimulation = userConfig.planType === 'simulation'

  const onTabChanged = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
  }

  // In bulk selection, Communication and Execution tabs are always disabled
  const notificationTabDisabled = true
  const executionTabDisabled = true

  // Tooltip logic:
  // - Configuration tab: no tooltip needed
  // - Notification/Execution tabs: always show tooltip in bulk selection (when disabled)
  const configurationTabTooltipDisabled = true
  const notificationTabTooltipDisabled = false // Always show tooltip since tab is always disabled
  const executionTabTooltipDisabled = false // Always show tooltip since tab is always disabled

  // Select the appropriate tooltip text based on the mode
  const notificationTooltipText = isSimulation
    ? texts.notificationTabDisabledInSimulationTooltip
    : texts.notificationTabDisabledInBulkSelectionTooltip
  const executionTooltipText = isSimulation
    ? texts.executionTabDisabledInSimulationTooltip
    : texts.executionTabDisabledInBulkSelectionTooltip

  return (
    <Tabs
      value={tab}
      onChange={onTabChanged}
      variant="fullWidth"
      sx={{
        '& .MuiTab-root': {
          color: 'text.secondary',
        },
        '& .Mui-selected': {
          color: 'primary.main',
        },
      }}
    >
      <Tab
        label={
          <Tooltip disabled={configurationTabTooltipDisabled} title="" placement="bottom">
            <>{texts.configuration}</>
          </Tooltip>
        }
        style={{ pointerEvents: 'auto' }} // Required to allow tooltip to work properly
        data-testid="order-form-tab__configuration"
        data-trackid="order-form-tab__configuration"
      />

      <Tab
        label={
          <Tooltip
            disabled={notificationTabTooltipDisabled}
            title={notificationTooltipText}
            placement="bottom"
          >
            <>{texts.communication}</>
          </Tooltip>
        }
        style={{ pointerEvents: 'auto' }} // Required to allow tooltip to work properly
        data-testid="order-form-tab__notification"
        data-trackid="order-form-tab__notification"
        disabled={notificationTabDisabled}
      />

      <Tab
        label={
          <Tooltip
            disabled={executionTabTooltipDisabled}
            title={executionTooltipText}
            placement="bottom"
          >
            <>{texts.execution}</>
          </Tooltip>
        }
        style={{ pointerEvents: 'auto' }} // Required to allow tooltip to work properly
        data-testid="order-form-tab__execution"
        data-trackid="order-form-tab__execution"
        disabled={executionTabDisabled}
      />
    </Tabs>
  )
}
